import { captureAttribution, getAttributionPayload } from '@/lib/attribution'

export type BackendLeadInput = {
  landingSource: string
  name: string
  phone?: string
  email?: string
  messageVolume?: string
  notes?: string
}

export class BackendLeadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'BackendLeadError'
  }
}

export function leadNotes(
  heading: string,
  fields: Array<[string, string | number | boolean | null | undefined]>,
): string {
  const lines = [heading]
  for (const [label, value] of fields) {
    if (value === null || value === undefined) continue
    const text = String(value).trim()
    if (text === '') continue
    lines.push(`${label}: ${text}`)
  }
  return lines.join('\n')
}

export function localDateISO(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export async function submitBackendLead(input: BackendLeadInput): Promise<unknown> {
  captureAttribution()
  const response = await fetch(`${import.meta.env.BASE_URL}api/submit-form.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      customer_name: input.name.trim(),
      customer_phone: (input.phone ?? '').trim(),
      customer_email: (input.email ?? '').trim(),
      message_volume: (input.messageVolume ?? '').trim() || 'website',
      preferred_date: '',
      preferred_time_slot: '',
      additional_notes: (input.notes ?? '').trim(),
      attribution: getAttributionPayload(),
      landing_source: input.landingSource,
    }),
  })

  const payload = (await response.json().catch(() => ({}))) as {
    error?: unknown
    detail?: unknown
  }

  // The proxy can surface a backend validation error ({ detail: [...] }) with a 2xx status.
  if (!response.ok || Array.isArray(payload.detail)) {
    const error =
      (typeof payload.error === 'string' && payload.error) ||
      (typeof payload.detail === 'string' && payload.detail) ||
      'Failed to submit'
    throw new BackendLeadError(error)
  }

  return payload
}
